import { useState } from "react";
import { Pencil } from "lucide-react";
import { ShippingAddressInterface } from "./ShippingAddressInterface";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import { ModalWrapper } from "../modals/ModalWrapper";
import { updateShippingAddress } from "../../services/whatsapp/whatsappService";
import { handleApi } from "../../utils/handleApi";
import toast from "react-hot-toast";

interface ShippingCardProps {
  shippingAddress: ShippingAddressInterface;
  zipCode?: string;
  confirmationId?: number;
  aiEnabled?: boolean;
  onAddressUpdated?: () => void;
}

export const ShippingCard = ({
  shippingAddress,
  zipCode,
  confirmationId,
  aiEnabled,
  onAddressUpdated,
}: ShippingCardProps) => {
  const { language } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    address: shippingAddress.address || "",
    address_detail: shippingAddress.address_detail || "",
    city: shippingAddress.city || "",
    department: shippingAddress.department || "",
  });

  const handleEdit = () => {
    if (aiEnabled) {
      toast.error(dictionary["ai-active-warning"]?.[language]);
      return;
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!confirmationId) return;
    setSaving(true);
    const { error } = await handleApi(
      updateShippingAddress(confirmationId, {
        address: form.address,
        addressDetail: form.address_detail,
        city: form.city,
        department: form.department,
      })
    );
    if (!error) {
      toast.success(dictionary["address-updated"]?.[language]);
      onAddressUpdated?.();
      setIsEditing(false);
    }
    setSaving(false);
  };

  return (
    <>
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{dictionary["shipping-address"][language]}</h2>
          {confirmationId && (
            <button
              onClick={handleEdit}
              className="text-gray-400 hover:text-gray-600 p-1"
              title={dictionary["edit-address"]?.[language]}
            >
              <Pencil size={16} />
            </button>
          )}
        </div>

        <div className="space-y-1 text-gray-700">
          <p className="font-medium">
            {shippingAddress.name}
            {shippingAddress.last_name && ` ${shippingAddress.last_name}`}
          </p>
          <p>{shippingAddress.address}</p>
          {shippingAddress.address_detail && <p>{shippingAddress.address_detail}</p>}
          {(shippingAddress.city || shippingAddress.department) && (
            <p>
              {shippingAddress.city}
              {shippingAddress.city && shippingAddress.department && " - "}
              {shippingAddress.department}
            </p>
          )}
          {shippingAddress.neighborhood && <p>{shippingAddress.neighborhood}</p>}
          {zipCode && (
            <p>
              <span className="font-medium">Zip Code:</span> {zipCode}
            </p>
          )}
        </div>
      </div>

      {isEditing && (
        <ModalWrapper isOpen onClose={() => setIsEditing(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">{dictionary["edit-address"]?.[language]}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {dictionary["shipping-address"]?.[language]}
                </label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {dictionary["address-detail"]?.[language] || "Detalle"}
                </label>
                <input
                  type="text"
                  value={form.address_detail}
                  onChange={(e) => setForm({ ...form, address_detail: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {dictionary["city"]?.[language] || "Ciudad"}
                </label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {dictionary["department"]?.[language] || "Departamento"}
                </label>
                <input
                  type="text"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                {dictionary["cancel"]?.[language]}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "..." : dictionary["save"]?.[language]}
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </>
  );
};
